import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDeviceFingerprint } from "./useDeviceFingerprint";
import { useIPGeolocation } from "./useIPGeolocation";

interface ActivityDetails {
  action: string;
  target_type?: string;
  target_id?: string;
  old_value?: any;
  new_value?: any;
  ip_address?: string;
  user_agent?: string;
  affected_user_id?: string;
  affected_user_email?: string;
}

export function useActivityLogger() {
  const deviceFingerprint = useDeviceFingerprint();
  const { geolocation } = useIPGeolocation();

  const calculateRiskScore = (activityType: string, details: ActivityDetails): number => {
    let risk = 0;
    
    // High risk for admin actions
    if (activityType.startsWith("admin_")) risk += 30;
    
    // Elevated risk for deletions
    if (activityType.includes("delete") || activityType.includes("ban")) risk += 20;
    
    // Medium risk for role changes
    if (activityType.includes("role_changed")) risk += 15;
    
    // Increase risk if no IP info available (could be VPN/proxy)
    if (!geolocation?.ip_address || geolocation.ip_address === "Unknown") risk += 10;
    
    return Math.min(risk, 100);
  };

  const getSeverity = (activityType: string): string => {
    if (activityType.includes("delete") || activityType.includes("ban")) return "critical";
    if (activityType.includes("role") || activityType.includes("publish")) return "high";
    if (activityType.startsWith("admin_")) return "medium";
    return "low";
  };

  const activityQueue = useRef<Array<{ activityType: string; details: ActivityDetails }>>([]);
  const flushTimeout = useRef<NodeJS.Timeout>();

  const logActivity = async (activityType: string, details: ActivityDetails) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      const riskScore = calculateRiskScore(activityType, details);
      const severity = getSeverity(activityType);

      // For critical activities, log immediately
      if (severity === "critical" || severity === "high") {
        const { error } = await supabase.from("user_activity").insert({
          user_id: user.id,
          activity_type: activityType,
          details: {
            ...details,
            timestamp: new Date().toISOString(),
            device_info: deviceFingerprint?.device_info || null,
          },
          ip_address: geolocation?.ip_address || "Unknown",
          user_agent: navigator.userAgent || "Unknown",
          geolocation: geolocation ? {
            country: geolocation.country,
            city: geolocation.city,
            region: geolocation.region,
            latitude: geolocation.latitude,
            longitude: geolocation.longitude,
          } : null,
          device_fingerprint: deviceFingerprint?.fingerprint_hash || null,
          risk_score: riskScore,
          action_severity: severity,
        });

        if (error) {
          console.error("Failed to insert activity log:", error);
        }
      } else {
        // For non-critical activities, batch them
        activityQueue.current.push({ activityType, details });
        
        // Clear existing timeout
        if (flushTimeout.current) {
          clearTimeout(flushTimeout.current);
        }
        
        // Flush queue after 5 seconds or when queue reaches 10 items
        if (activityQueue.current.length >= 10) {
          await flushActivityQueue();
        } else {
          flushTimeout.current = setTimeout(flushActivityQueue, 5000);
        }
      }
    } catch (error) {
      console.error("Failed to log activity:", error);
    }
  };

  const flushActivityQueue = async () => {
    if (activityQueue.current.length === 0) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const activities = activityQueue.current.map(({ activityType, details }) => ({
      user_id: user.id,
      activity_type: activityType,
      details: {
        ...details,
        timestamp: new Date().toISOString(),
        device_info: deviceFingerprint?.device_info || null,
      },
      ip_address: geolocation?.ip_address || "Unknown",
      user_agent: navigator.userAgent || "Unknown",
      geolocation: geolocation ? {
        country: geolocation.country,
        city: geolocation.city,
        region: geolocation.region,
        latitude: geolocation.latitude,
        longitude: geolocation.longitude,
      } : null,
      device_fingerprint: deviceFingerprint?.fingerprint_hash || null,
      risk_score: calculateRiskScore(activityType, details),
      action_severity: getSeverity(activityType),
    }));

    try {
      await supabase.from("user_activity").insert(activities);
      activityQueue.current = [];
    } catch (error) {
      console.error("Failed to flush activity queue:", error);
    }
  };

  return { logActivity };
}

// Hook for tracking lesson study time
export function useLessonTimeTracker(lessonId: string | null, enrollmentId: string | null) {
  const startTimeRef = useRef<number>(Date.now());
  const accumulatedTimeRef = useRef<number>(0);
  const lastActivityRef = useRef<number>(Date.now());
  const saveIntervalRef = useRef<NodeJS.Timeout>();
  const idleTimeoutRef = useRef<NodeJS.Timeout>();

  const isIdle = useRef<boolean>(false);
  const IDLE_THRESHOLD = 30000; // 30 seconds
  const SAVE_INTERVAL = 120000; // 2 minutes

  // Save accumulated time to database
  const saveTimeToDatabase = useCallback(async () => {
    if (!lessonId || !enrollmentId || accumulatedTimeRef.current === 0) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const minutesToAdd = Math.floor(accumulatedTimeRef.current / 60000);
      if (minutesToAdd === 0) return;

      // Update lesson_progress time_spent
      const { data: currentProgress } = await supabase
        .from("lesson_progress")
        .select("time_spent")
        .eq("enrollment_id", enrollmentId)
        .eq("lesson_id", lessonId)
        .single();

      const currentTimeSpent = currentProgress?.time_spent || 0;
      const newTimeSpent = currentTimeSpent + minutesToAdd;

      await supabase
        .from("lesson_progress")
        .update({ time_spent: newTimeSpent })
        .eq("enrollment_id", enrollmentId)
        .eq("lesson_id", lessonId);

      // Update daily activity
      const today = new Date().toISOString().split('T')[0];
      const { data: dailyActivity } = await supabase
        .from("user_daily_activity")
        .select("*")
        .eq("user_id", user.id)
        .eq("activity_date", today)
        .single();

      if (dailyActivity) {
        await supabase
          .from("user_daily_activity")
          .update({
            total_minutes: dailyActivity.total_minutes + minutesToAdd,
          })
          .eq("id", dailyActivity.id);
      } else {
        await supabase.from("user_daily_activity").insert({
          user_id: user.id,
          activity_date: today,
          total_minutes: minutesToAdd,
          lessons_started: 1,
        });
      }

      accumulatedTimeRef.current = 0;
      startTimeRef.current = Date.now();
    } catch (error) {
      console.error("Failed to save time to database:", error);
    }
  }, [lessonId, enrollmentId]);

  // Reset idle state on user activity
  const handleUserActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (isIdle.current) {
      isIdle.current = false;
      startTimeRef.current = Date.now();
    }

    // Clear and reset idle timeout
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    idleTimeoutRef.current = setTimeout(() => {
      isIdle.current = true;
    }, IDLE_THRESHOLD);
  }, []);

  useEffect(() => {
    if (!lessonId || !enrollmentId) return;

    // Track active time
    const trackTime = () => {
      if (!isIdle.current) {
        const now = Date.now();
        const elapsed = now - startTimeRef.current;
        accumulatedTimeRef.current += elapsed;
        startTimeRef.current = now;
      }
    };

    // Set up activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    // Initial idle timeout
    idleTimeoutRef.current = setTimeout(() => {
      isIdle.current = true;
    }, IDLE_THRESHOLD);

    // Save time periodically
    saveIntervalRef.current = setInterval(() => {
      trackTime();
      saveTimeToDatabase();
    }, SAVE_INTERVAL);

    // Save on unmount
    return () => {
      trackTime();
      saveTimeToDatabase();
      
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });

      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, [lessonId, enrollmentId, handleUserActivity, saveTimeToDatabase]);

  return { saveTimeToDatabase };
}

// Activity type constants
export const ACTIVITY_TYPES = {
  // User management
  USER_BANNED: "admin_user_banned",
  USER_UNBANNED: "admin_user_unbanned",
  USER_ROLE_CHANGED: "admin_user_role_changed",
  USER_DELETED: "admin_user_deleted",
  
  // Course management
  COURSE_CREATED: "admin_course_created",
  COURSE_UPDATED: "admin_course_updated",
  COURSE_DELETED: "admin_course_deleted",
  COURSE_PUBLISHED: "admin_course_published",
  COURSE_UNPUBLISHED: "admin_course_unpublished",
  
  // Content management
  LESSON_CREATED: "admin_lesson_created",
  LESSON_UPDATED: "admin_lesson_updated",
  LESSON_DELETED: "admin_lesson_deleted",
  
  // Communication
  ANNOUNCEMENT_CREATED: "admin_announcement_created",
  ANNOUNCEMENT_DELETED: "admin_announcement_deleted",
  MESSAGE_SENT: "admin_message_sent",
  
  // Promotions & Discounts
  PROMOTION_CREATED: "admin_promotion_created",
  PROMOTION_UPDATED: "admin_promotion_updated",
  PROMOTION_DELETED: "admin_promotion_deleted",
  DISCOUNT_CREATED: "admin_discount_created",
  DISCOUNT_UPDATED: "admin_discount_updated",
  DISCOUNT_DELETED: "admin_discount_deleted",
  
  // Blog management
  BLOG_POST_CREATED: "admin_blog_created",
  BLOG_POST_UPDATED: "admin_blog_updated",
  BLOG_POST_DELETED: "admin_blog_deleted",
  BLOG_POST_PUBLISHED: "admin_blog_published",
  
  // Feedback
  FEEDBACK_RESPONDED: "admin_feedback_responded",
  FEEDBACK_STATUS_CHANGED: "admin_feedback_status_changed",
  
  // System
  SETTINGS_CHANGED: "admin_settings_changed",
  ADMIN_LOGIN: "admin_login",
  ADMIN_LOGOUT: "admin_logout",
  UNAUTHORIZED_ACCESS: "admin_unauthorized_access",
};
